import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { cn } from '../../lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Clock, AlertTriangle, User, Trash2, XCircle, Pencil, CalendarIcon, Video } from 'lucide-react';
import { APPOINTMENT_STATES } from '../../utils/mockData';
import { calcDuration, calcEndTime } from '../../utils/calendarHelpers';
import { DURATION_OPTIONS, MOTIVOS_PRESET, MODALIDAD_OPTIONS } from '../../utils/calendarConstants';
import { ConsultaPagoPanel } from './ConsultaPagoPanel';

/**
 * Panel lateral (Sheet) con el detalle de una cita y su formulario de edición.
 * Componente controlado: el estado vive en CalendarPage.
 */
const ESTADO_FLUJO = ['citado', 'confirmada', 'en_sala', 'atendido'];
const ESTADO_SALIDA = ['cancelada', 'no_asistio'];

export const AppointmentDetailSheet = ({
  open, setOpen,
  selectedAppointment,
  isEditMode, setIsEditMode,
  editData, setEditData,
  doctors, patients,
  editTimeSlots, occupiedEdit, editConflict,
  calendarEditOpen, setCalendarEditOpen,
  onOpenEdit, onViewPatient, onCancelApt, onDeleteApt, onSaveEdit,
  onUpdateEstado, onTogglePago,
  clinicPhone, clinicName,
}) => {
  return (
    <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (!o) setIsEditMode(false); }}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="flex flex-row items-center justify-between pr-8">
          <SheetTitle>{isEditMode ? 'Editar Cita' : 'Detalles de la Cita'}</SheetTitle>
          {selectedAppointment && !isEditMode && (
            <Button variant="outline" size="sm" onClick={onOpenEdit} className="gap-1.5">
              <Pencil className="h-3.5 w-3.5" /> Editar
            </Button>
          )}
        </SheetHeader>

        {selectedAppointment && !isEditMode && (
          <div className="mt-6 space-y-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">{selectedAppointment.paciente_nombre}</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedAppointment.fecha} · {selectedAppointment.hora_inicio} – {selectedAppointment.hora_fin}</span>
                  <Badge variant="outline" className="text-[10px] ml-auto">
                    {calcDuration(selectedAppointment.hora_inicio, selectedAppointment.hora_fin)} min
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedAppointment.doctor_color }} />
                  <span>{selectedAppointment.doctor_nombre}</span>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Motivo</p>
                <p className="font-medium">{selectedAppointment.motivo}</p>
              </div>
              {selectedAppointment.notas && (
                <div>
                  <p className="text-sm text-muted-foreground">Notas</p>
                  <p className="text-sm">{selectedAppointment.notas}</p>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={APPOINTMENT_STATES[selectedAppointment.estado]?.color}>
                  {APPOINTMENT_STATES[selectedAppointment.estado]?.label}
                </Badge>
                {selectedAppointment.hora_por_confirmar && (
                  <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
                    Hora por confirmar
                  </Badge>
                )}
              </div>

              {/* Liga de Google Meet, solo para citas en línea */}
              {selectedAppointment.modalidad === 'en_linea' && (
                selectedAppointment.meet_link ? (
                  <a
                    href={selectedAppointment.meet_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                  >
                    <Video className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{selectedAppointment.meet_link}</span>
                  </a>
                ) : (
                  <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-muted text-muted-foreground">
                    <Video className="h-4 w-4 flex-shrink-0" />
                    Consulta en línea sin liga de Meet generada todavía
                  </div>
                )
              )}

              {/* Seguimiento automático: la fecha está apartada pero la hora es
                  tentativa hasta que el paciente la acepte con el bot. */}
              {selectedAppointment.hora_por_confirmar && (
                <p className="text-xs text-muted-foreground border-l-2 border-amber-400 pl-2">
                  Cita de seguimiento agendada automáticamente. El bot le preguntó al
                  paciente si le acomoda este horario, así que puede cambiar.
                </p>
              )}

              <ConsultaPagoPanel
                appointment={selectedAppointment}
                clinicPhone={clinicPhone}
                clinicName={clinicName}
                onTogglePago={onTogglePago}
              />

              {/* Cambio rápido de estado */}
              {onUpdateEstado && !['atendido', 'cancelada', 'no_asistio'].includes(selectedAppointment.estado) && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground font-medium">Cambiar estado</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ESTADO_FLUJO.filter(e => e !== selectedAppointment.estado).map(e => (
                      <button
                        key={e}
                        onClick={() => onUpdateEstado(selectedAppointment, e)}
                        className={cn('text-xs px-2.5 py-1 rounded-full border transition-colors hover:opacity-80', APPOINTMENT_STATES[e]?.color)}
                      >
                        {APPOINTMENT_STATES[e]?.label}
                      </button>
                    ))}
                    {ESTADO_SALIDA.map(e => (
                      <button
                        key={e}
                        onClick={() => onUpdateEstado(selectedAppointment, e)}
                        className={cn('text-xs px-2.5 py-1 rounded-full border transition-colors hover:opacity-80', APPOINTMENT_STATES[e]?.color)}
                      >
                        {APPOINTMENT_STATES[e]?.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Alertas médicas */}
              {(() => {
                const patient = patients.find(p => p.id === selectedAppointment.paciente_id);
                if (!patient?.alertas_medicas?.length) return null;
                return (
                  <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900">
                    <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400 mb-2">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="font-medium text-sm">Alertas Médicas</span>
                    </div>
                    <ul className="space-y-1">
                      {patient.alertas_medicas.map((alerta, i) => (
                        <li key={i} className="text-sm text-rose-700 dark:text-rose-300">· {alerta.descripcion}</li>
                      ))}
                    </ul>
                  </div>
                );
              })()}
            </div>

            <div className="space-y-2">
              <Button className="w-full" onClick={onOpenEdit} data-testid="edit-appointment-btn">
                <Pencil className="h-4 w-4 mr-2" /> Editar Cita
              </Button>
              <Button variant="outline" className="w-full" onClick={() => onViewPatient(selectedAppointment)} data-testid="view-patient-btn">
                <User className="h-4 w-4 mr-2" /> Ver Expediente Completo
              </Button>
              {selectedAppointment.estado !== 'cancelada' && (
                <Button
                  variant="outline"
                  className="w-full text-amber-600 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                  onClick={() => onCancelApt(selectedAppointment)}
                >
                  <XCircle className="h-4 w-4 mr-2" /> Cancelar Cita
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={() => onDeleteApt(selectedAppointment)}
              >
                <Trash2 className="h-4 w-4 mr-2" /> Eliminar Cita
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setOpen(false)}>
                Cerrar
              </Button>
            </div>
          </div>
        )}

        {/* ── Formulario de edición ── */}
        {selectedAppointment && isEditMode && editData && (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              Paciente: <span className="font-medium text-foreground">{selectedAppointment.paciente_nombre}</span>
            </p>

            {/* Doctor */}
            <div className="space-y-2">
              <Label>Doctor</Label>
              <Select value={editData.doctor_id} onValueChange={v => setEditData({ ...editData, doctor_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {doctors.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                        {d.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Fecha */}
            <div className="space-y-2">
              <Label>Fecha</Label>
              <Popover open={calendarEditOpen} onOpenChange={setCalendarEditOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editData.fecha ? format(new Date(editData.fecha + 'T00:00:00'), 'PPP', { locale: es }) : 'Seleccionar'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={editData.fecha ? new Date(editData.fecha + 'T00:00:00') : undefined}
                    onSelect={(date) => {
                      if (date) { setEditData({ ...editData, fecha: format(date, 'yyyy-MM-dd') }); setCalendarEditOpen(false); }
                    }}
                    locale={es}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Hora + Duración */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Hora inicio</Label>
                <Select value={editData.hora_inicio} onValueChange={v => setEditData({ ...editData, hora_inicio: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {editTimeSlots.map(s => (
                      <SelectItem key={s} value={s} disabled={occupiedEdit.has(s)}>
                        {s}{occupiedEdit.has(s) ? ' · ocupado' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Duración</Label>
                <Select value={editData.duracion.toString()} onValueChange={v => setEditData({ ...editData, duracion: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DURATION_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value.toString()}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Preview hora fin */}
            <div className="flex items-center gap-2 text-sm bg-muted/50 px-3 py-2 rounded-lg">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Termina:</span>
              <span className="font-semibold">{calcEndTime(editData.hora_inicio, editData.duracion)}</span>
              <span className="text-muted-foreground ml-auto">{editData.duracion} min</span>
            </div>

            {/* Alerta de conflicto en edición */}
            {editConflict && (
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-semibold text-amber-800 dark:text-amber-300">Horario ocupado</p>
                  <p className="text-amber-700 dark:text-amber-400 text-xs mt-0.5">
                    {editConflict.paciente_nombre} ya tiene cita de {editConflict.hora_inicio}–{editConflict.hora_fin}
                  </p>
                </div>
              </div>
            )}

            {/* Modalidad */}
            <div className="space-y-2">
              <Label>Modalidad</Label>
              <Select value={editData.modalidad} onValueChange={v => setEditData({ ...editData, modalidad: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODALIDAD_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label} · ${opt.costo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Estado */}
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select value={editData.estado} onValueChange={v => setEditData({ ...editData, estado: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(APPOINTMENT_STATES).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Motivo */}
            <div className="space-y-2">
              <Label>Motivo *</Label>
              <div className="flex flex-wrap gap-1.5">
                {MOTIVOS_PRESET.map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setEditData({ ...editData, motivo: m })}
                    className={cn(
                      'text-xs px-2 py-1 rounded-full border transition-colors',
                      editData.motivo === m
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'border-border/50 hover:border-border text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {m}
                  </button>
                ))}
              </div>
              <Input
                value={editData.motivo}
                onChange={e => setEditData({ ...editData, motivo: e.target.value })}
                placeholder="Motivo de la consulta"
              />
            </div>

            {/* Notas */}
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={editData.notas}
                onChange={e => setEditData({ ...editData, notas: e.target.value })}
                rows={2}
                placeholder="Notas adicionales..."
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setIsEditMode(false)}>
                Cancelar
              </Button>
              <Button
                className="flex-1"
                onClick={onSaveEdit}
                disabled={!!editConflict}
              >
                Guardar Cambios
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
